import React, { Component } from 'react'
import isEqual from 'lodash/isEqual'

import { PopupCenter } from 'lib/utils'
import RoutesHelpers from 'helpers/RoutesHelper'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { selectors as AuthSelectors } from 'data/auth'
import { selectors as ConfigSelectors } from 'data/config'
import { selectors as UserPreferencesSelectors } from 'data/userPreferences'
import { providesUserPreferencesMutations } from 'components/Settings/User/dataSync'
import {
  closeActiveModal,
  createInfoToasterMessage,
  createErrorToasterMessage,
} from 'data/ui/actions'

import FilesApi from 'remote/files'
import UsersApi from 'remote/users'
import { uploadToGoogle } from './files/utils'

import FromGoogle from './FromGoogle'
import ConfluenceSpacesList from './confluence/SpacesList'
import ConfluenceSettingsForm from './confluence/SettingsForm'
import ChannelListContainer from './channelListContainer'
import SourceContainer from './sourceContainer'
import ImportGuide from './importGuide'
import BreadCrumbs from './breadcrumbs'

import { Button, Text } from 'components/base'
import FileUpload from 'components/Utils/FileUpload'
import ModalBody from 'components/Utils/Modal/Body'

const FILE_MAX_SIZE = 5e6 // 5MB

class ImportFiles extends Component {
  // A reference to the FileUploader's dropzone instance
  dropzone = null

  state = {
    page: null,
    guide: null,
    googleDriveToken: null,
    uploadFormat: '',
    creationSource: 'manual_upload',
    channel: {},
    confluenceApiAuthenticationSettings: null,
    isConfluenceApiAuthenticationSettingsPristine: true,
    isEditingConfluenceSettings: false,
    confluenceSelectedSpaceKey: null,
  }

  constructor(props) {
    super(props)

    this.handleFileUpload = this.handleFileUpload.bind(this)
    this.handleGoogleDriveImport = this.handleGoogleDriveImport.bind(this)
  }

  async fetchGoogleDriveToken() {
    const { apiToken, apiEndpoint } = this.props

    let googleDriveToken = null

    try {
      googleDriveToken = await UsersApi.getGoogleDriveAuthToken({
        apiToken,
        apiEndpoint,
      })
    } catch (error) {
      // Error can happen while fetching token. Let it close the modal
    }

    return googleDriveToken
  }

  async componentDidMount() {
    const { confluenceApiAuthentication } = this.props
    this.setState({
      googleDriveToken: await this.fetchGoogleDriveToken(),
      isEditingConfluenceSettings: !Boolean(confluenceApiAuthentication),
      confluenceApiAuthenticationSettings: confluenceApiAuthentication
        ? confluenceApiAuthentication.toJS()
        : {},
    })
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.isEditingConfluenceSettings &&
      !isEqual(
        prevProps.confluenceApiAuthentication,
        this.props.confluenceApiAuthentication
      )
    ) {
      this.setState({
        isEditingConfluenceSettings: false,
      })
    }
  }

  async handleGoogleDriveImport() {
    const { googleDriveToken } = this.state

    if (googleDriveToken) {
      return this.setState({ page: 'google' })
    }

    PopupCenter({
      url: RoutesHelpers.googleDriveFileImport({
        apiEndpoint: this.props.apiEndpoint,
        apiToken: this.props.apiToken,
      }),
      title: 'import/google-drive',
      requestedWidth: 590,
      requestedHeight: 600,
      onClose: async () => {
        const googleDriveToken = await this.fetchGoogleDriveToken()

        if (!googleDriveToken) {
          // * Auth cancelled by user or in error for some other reason
          this.props.closeActiveModal()
          return
        }

        this.setState({ page: 'google', googleDriveToken })
      },
    })
  }

  async handleFileUpload({ files }) {
    this.props.closeActiveModal()
    this.props.createInfoToasterMessage({
      title: 'Files import',
      detail: `Your data will begin importing shortly. You will be notified when we're done.`,
    })

    const { apiToken, apiEndpoint } = this.props
    try {
      const filesWithUrls = await Promise.all(
        files.map(async file => {
          return {
            file,
            url: await uploadToGoogle({ apiEndpoint, apiToken }, { file }),
          }
        })
      )

      FilesApi.uploadTextFiles(
        { apiToken, apiEndpoint },
        {
          input: {
            channelId: this.state.channel.id,
            fileList: filesWithUrls
              // remove null items
              .filter(item => item)
              .map(({ url, file }) => {
                let type = 'markdown'

                if (
                  file.type ===
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                  file.name.endsWith('.docx')
                ) {
                  type = 'docx'
                }

                if (
                  file.type === 'application/msword' ||
                  file.name.endsWith('.doc')
                ) {
                  type = 'doc'
                }

                if (file.type === 'text/html') {
                  type = 'html'
                }

                return {
                  url,
                  name: file.name,
                  type,
                  creationSource: this.state.creationSource,
                }
              }),
          },
        }
      )
    } catch (error) {
      this.props.createErrorToasterMessage({
        title: 'An error occured while importing your data.',
        detail: 'Please try again or reach out to the support for help.',
      })
    }
  }

  handleGuideSelection = (serviceName, { uploadFormat, creationSource }) => {
    this.setState({
      page: 'guide',
      serviceName,
      uploadFormat,
      creationSource,
    })
  }

  handleManualImport = uploadFormat => {
    this.setState({ uploadFormat, creationSource: 'manual_upload' }, () =>
      this.dropzone.open()
    )
  }

  handleConfluenceImport = () => {
    const { apiToken, apiEndpoint } = this.props
    FilesApi.importConfluenceSpace(
      { apiToken, apiEndpoint },
      {
        input: {
          spaceKey: this.state.confluenceSelectedSpaceKey,
          channelId: this.state.channel.id,
        },
      }
    )
      .then(() =>
        this.props.createInfoToasterMessage({
          title: 'Confluence import',
          detail: `Your confluence data will begin importing shortly. You will be notified when we're done.`,
        })
      )
      .catch(() =>
        this.props.createErrorToasterMessage({
          title: 'An error occured while importing your data.',
          detail: 'Please try again or reach out to the support for help.',
        })
      )
    this.props.closeActiveModal()
  }

  handleConfluenceSelected = () =>
    this.setState({
      page: 'confluence',
    })

  handleConfluenceSpaceSelected = key =>
    this.setState({
      confluenceSelectedSpaceKey: key,
    })

  updateConfluenApiAuthenticationSettings = () => {
    if (this.state.isConfluenceApiAuthenticationSettingsPristine) {
      this.setState({
        isEditingConfluenceSettings: false,
      })
      return
    }
    this.props.updateUserPreferences({
      confluenceApiAuthentication: this.state
        .confluenceApiAuthenticationSettings,
    })
  }

  handleConfluenceSettingsUpdate = confluenceApiAuthenticationSettings => {
    this.setState({
      confluenceApiAuthenticationSettings,
      isConfluenceApiAuthenticationSettingsPristine: isEqual(
        confluenceApiAuthenticationSettings,
        this.props.confluenceApiAuthentication.toJS()
      ),
    })
  }

  enableConfluenceSettingsEdit = () =>
    this.setState({
      isEditingConfluenceSettings: true,
      confluenceSelectedSpaceKey: null,
    })

  getPageFromState() {
    const {
      googleDriveToken,
      channel,
      serviceName,
      confluenceApiAuthenticationSettings,
      isEditingConfluenceSettings,
    } = this.state

    const { hasConfluenceImportFeatureFlag } = this.props

    switch (this.state.page) {
      case 'source':
        return (
          <SourceContainer
            onGoogleDriveImport={this.handleGoogleDriveImport}
            onManualImport={this.handleManualImport}
            onConfluenceSelected={this.handleConfluenceSelected}
            onGuideSelected={this.handleGuideSelection}
            hasConfluenceImportFeatureFlag={hasConfluenceImportFeatureFlag}
          />
        )
      case 'google':
        return <FromGoogle channel={channel} accessToken={googleDriveToken} />
      case 'confluence':
        return isEditingConfluenceSettings ? (
          <ConfluenceSettingsForm
            onChange={this.handleConfluenceSettingsUpdate}
            values={confluenceApiAuthenticationSettings}
          />
        ) : (
          <ConfluenceSpacesList
            onSpaceSelected={this.handleConfluenceSpaceSelected}
            onSettingsEdit={this.handleSettingsEdition}
          />
        )

      case 'guide':
        return <ImportGuide serviceName={serviceName} />
      default:
        return (
          <ChannelListContainer
            onChannelSelected={channel =>
              this.setState({ channel, page: 'source' })
            }
          />
        )
    }
  }

  getBreadcrumbsStep() {
    switch (this.state.page) {
      case 'source':
        return 2
      case 'guide':
        return 3
      case 'confluence':
        return 3
      default:
        return 1
    }
  }

  handleGoToPage = page => {
    this.setState({
      page,
    })
  }

  render() {
    const {
      page,
      confluenceApiAuthenticationSettings,
      isEditingConfluenceSettings,
    } = this.state
    const isConfluenceSettingsValid =
      confluenceApiAuthenticationSettings &&
      Object.values(confluenceApiAuthenticationSettings).every(value => value)
    return (
      <ModalBody>
        <BreadCrumbs
          step={this.getBreadcrumbsStep()}
          onSelected={this.handleGoToPage}
        />
        {this.getPageFromState()}
        <FileUpload
          accept={this.state.uploadFormat}
          multiple={true}
          maxSize={FILE_MAX_SIZE}
          onRef={dropzone => {
            this.dropzone = dropzone
          }}
          onUpload={this.handleFileUpload}
          renderContent={() => null}
        />
        {page === 'confluence' &&
          !isEditingConfluenceSettings && (
            <Text.Action
              className="primary margin-top-small"
              onClick={this.enableConfluenceSettingsEdit}
            >
              Edit your confluence settings
            </Text.Action>
          )}
        <section className="flexbox flexbox-space-between">
          <Button.Secondary
            onClick={this.props.closeActiveModal}
            className="margin-top-larger"
          >
            Cancel
          </Button.Secondary>
          {page === 'guide' && (
            <Button.Primary
              onClick={() => this.dropzone.open()}
              className="margin-top-larger"
            >
              Upload Content
            </Button.Primary>
          )}
          {page === 'confluence' &&
            this.state.confluenceSelectedSpaceKey && (
              <Button.Primary
                onClick={this.handleConfluenceImport}
                className="margin-top-larger"
              >
                {`Import to ${this.state.channel.name}`}
              </Button.Primary>
            )}
          {page === 'confluence' &&
            isEditingConfluenceSettings && (
              <Button.Primary
                disabled={!isConfluenceSettingsValid}
                onClick={this.updateConfluenApiAuthenticationSettings}
                className="margin-top-larger"
              >
                Next
              </Button.Primary>
            )}
        </section>
      </ModalBody>
    )
  }
}

export default compose(
  providesUserPreferencesMutations,
  connect(
    function(state) {
      return {
        apiToken: AuthSelectors.getApiToken(state),
        apiEndpoint: ConfigSelectors.getApiEndpoint(state),
        hasConfluenceImportFeatureFlag: ConfigSelectors.hasFlag(
          state,
          'confluence-import'
        ),
        confluenceApiAuthentication: UserPreferencesSelectors.getUserPreferences(
          state
        ).get('confluenceApiAuthentication'),
      }
    },
    {
      closeActiveModal,
      createErrorToasterMessage,
      createInfoToasterMessage,
    }
  )
)(ImportFiles)
